<template>
  <v-card
    v-if="isTenantAdmin"
    flat
    class="security-information pa-3"
  >
    <v-divider class="mb-3" />

    <div class="subtitle-1 font-weight-medium mb-3">
      {{ $t('security_information') }}
    </div>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
    />

    <template v-else>
      <v-list dense class="transparent pa-0">
        <v-list-item class="px-0">
          <v-list-item-content>
            <v-list-item-subtitle>{{ $t('grants') }}</v-list-item-subtitle>
            <v-list-item-title class="security-value">
              {{ grantNames.length ? grantNames.join(', ') : '—' }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item class="px-0">
          <v-list-item-content>
            <v-list-item-subtitle>{{ $t('attached_security_groups') }}</v-list-item-subtitle>
            <v-list-item-title class="security-value">
              {{ attachedSecurityGroupNames.length ? attachedSecurityGroupNames.join(', ') : '—' }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item class="px-0">
          <v-list-item-content>
            <v-list-item-subtitle>{{ $t('security_classification') }}</v-list-item-subtitle>
            <v-list-item-title class="security-value">
              {{ classificationNames.length ? classificationNames.join(', ') : '—' }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

      </v-list>

      <v-divider class="my-3" />

      <div class="subtitle-2 font-weight-medium mb-2">
        {{ $t('groups_with_clearance') }}
      </div>
      <v-list-item-subtitle class="security-value">
        {{ clearanceGroupNames.length ? clearanceGroupNames.join(', ') : '—' }}
      </v-list-item-subtitle>
    </template>
  </v-card>
</template>

<style scoped>
.security-information {
  min-width: 280px;
}

.security-value {
  white-space: normal;
  overflow-wrap: anywhere;
}

</style>

<script>
import { mapActions, mapGetters } from 'vuex'

import UserActions from '@/store/core/user/actions-types'
import GroupActions from '@/store/core/group/actions-types'
import GrantActions from '@/store/node/grant/actions-types'
import ClassificationActions from '@/store/operation/classification/actions-types'
import ClearanceActions from '@/store/operation/clearance/actions-types'
import SecurityGroupActions from '@/store/operation/security_group/actions-types'

export default {
  name: 'NodeSecurityInformation',

  props: {
    node: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    loading: false,
    users: [],
    groups: [],
    grants: [],
    attachedSecurityGroups: [],
    securityGroups: [],
    classifications: [],
    clearances: [],
    marks: [],
    loadRequestId: 0,
  }),

  computed: {
    ...mapGetters('core/auth', ['checkIsAdminInTenant']),

    isTenantAdmin() {
      return this.checkIsAdminInTenant(this.node.tenant_id)
    },

    grantNames() {
      return this.unique(this.grants.map(grant => {
        return grant.user_id ? this.userName(grant.user_id) : this.groupName(grant.group_id)
      }))
    },

    classificationNames() {
      return this.unique(this.classifications.map(classification => {
        return this.classificationLabel(classification)
      }))
    },

    attachedSecurityGroupNames() {
      return this.unique(this.attachedSecurityGroups.map(group => {
        return this.securityGroupLabel(group)
      }))
    },

    clearanceGroupNames() {
      const classifiedMarkIds = new Set(this.classifications.map(item => item.security_mark_id))
      const groupIds = this.clearances
        .filter(clearance => classifiedMarkIds.has(clearance.security_mark_id))
        .map(clearance => clearance.group_id)

      return this.unique(groupIds.map(groupId => this.groupName(groupId)))
    },
  },

  watch: {
    node: {
      immediate: true,
      deep: true,
      handler() {
        this.fetchSecurityInformation()
      },
    },
  },

  methods: {
    ...mapActions('core/user', [UserActions.GET_ALL_USERS]),
    ...mapActions('core/group', [GroupActions.GET_ALL_GROUPS]),
    ...mapActions('node/grant', [GrantActions.GET_GRANTS_BY_NODE_WITH_PARENTS]),
    ...mapActions('operation/classification', [ClassificationActions.GET_CLASSIFICATION_RECURSIVE_BY_NODE]),
    ...mapActions('operation/clearance', [ClearanceActions.GET_CLEARANCE_BY_GROUP]),
    ...mapActions('operation/security_group', [
      SecurityGroupActions.GET_SECURITY_GROUPS_BY_NODE,
      SecurityGroupActions.GET_SECURITY_GROUPS_BY_TENANT,
      SecurityGroupActions.GET_SECURITY_MARKS_BY_SECURITY_GROUP,
    ]),

    async fetchSecurityInformation() {
      if (!this.isTenantAdmin || !this.node.node_id) {
        return
      }

      const requestId = ++this.loadRequestId
      this.loading = true

      const tenantOptions = {
        'filter[tenant]': this.node.tenant_id,
        'filter[include_foreign]': true,
        'limit': 9999,
      }
      const userOptions = {
        ...tenantOptions,
        'filter[tenant_id]': this.node.tenant_id,
      }

      try {
        const [grantsResult, attachedSecurityGroupsResult, classificationsResult, usersResult, groupsResult, securityGroupsResult] = await Promise.all([
          this[GrantActions.GET_GRANTS_BY_NODE_WITH_PARENTS](this.node.node_id),
          this[SecurityGroupActions.GET_SECURITY_GROUPS_BY_NODE](this.node.node_id),
          this[ClassificationActions.GET_CLASSIFICATION_RECURSIVE_BY_NODE](this.node.node_id),
          this[UserActions.GET_ALL_USERS](userOptions),
          this[GroupActions.GET_ALL_GROUPS](tenantOptions),
          this[SecurityGroupActions.GET_SECURITY_GROUPS_BY_TENANT](this.node.tenant_id),
        ])

        const securityGroups = securityGroupsResult.items || securityGroupsResult || []
        const groups = groupsResult.items || []
        const [markResults, clearanceResults] = await Promise.all([
          Promise.all(securityGroups.map(group => {
            return this[SecurityGroupActions.GET_SECURITY_MARKS_BY_SECURITY_GROUP](group.security_group_id)
          })),
          Promise.all(groups.map(group => {
            return this[ClearanceActions.GET_CLEARANCE_BY_GROUP](group.user_group_id)
          })),
        ])

        if (requestId !== this.loadRequestId) {
          return
        }

        this.grants = [...(grantsResult.current || []), ...(grantsResult.parents || [])]
        this.attachedSecurityGroups = attachedSecurityGroupsResult.items || attachedSecurityGroupsResult || []
        this.classifications = classificationsResult || []
        this.clearances = clearanceResults.flatMap((result, index) => {
          return (result.items || result || []).map(clearance => ({
            ...clearance,
            group_id: clearance.group_id || groups[index].user_group_id,
          }))
        })
        this.users = usersResult.items || []
        this.groups = groups
        this.securityGroups = securityGroups
        this.marks = markResults.flatMap(result => result.items || result || [])
      } catch (error) {
        if (requestId === this.loadRequestId) {
          this.grants = []
          this.attachedSecurityGroups = []
          this.securityGroups = []
          this.classifications = []
          this.clearances = []
          this.marks = []
        }
      } finally {
        if (requestId === this.loadRequestId) {
          this.loading = false
        }
      }
    },

    unique(items) {
      return [...new Set(items.filter(Boolean))]
    },

    securityItemLabel(name, isRecursive) {
      if (name === '—') {
        return name
      }

      return `${name} (${this.$t(isRecursive ? 'recursive' : 'local')})`
    },

    classificationLabel(classification) {
      const mark = this.marks.find(item => item.security_mark_id === classification.security_mark_id)
      const securityGroupId = classification.security_group_id || mark?.security_group_id
      const securityGroup = this.securityGroups.find(group => group.security_group_id === securityGroupId)
      const markName = mark?.security_mark_key || '—'
      const securityGroupName = securityGroup?.security_group_key || '—'

      const source = this.$t(classification.is_recursive ? 'inherited' : 'local')

      return `${securityGroupName}: ${markName} (${source})`
    },

    securityGroupLabel(group) {
      if (!group.security_group_key) {
        return '—'
      }

      if (group.inherited) {
        return `${group.security_group_key} (${this.$t('inherited')})`
      }

      return this.securityItemLabel(group.security_group_key, group.recursive)
    },

    userName(userId) {
      return this.users.find(user => user.users_id === userId)?.name || '—'
    },

    groupName(groupId) {
      return this.groups.find(group => group.user_group_id === groupId)?.group_title || '—'
    },

    markName(markId) {
      return this.marks.find(mark => mark.security_mark_id === markId)?.security_mark_key || '—'
    },
  },
}
</script>
